import {HttpErrorResponse} from '@angular/common/http';
import {Component, computed, Signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {FormGroup, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {SubsectionScrollDirective} from '@app/directives/subsection-scroll/subsection-scroll.directive';
import {ROUTE_BASE} from '@app/routing/routes';
import {OwnerTableFormFieldComponent} from '@forms/owner-table-form-field/owner-table-form-field.component';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {OrganizationService, ResourceCreate, ResourceResponse, ResourceService, UiConfigService} from '@middleware';
import {ResourcePermissionsFlags} from '@models/search';
import {FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {AttestationService} from '@services/attestation/attestation.service';
import {UserService} from '@services/user/user.service';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {FormlyFieldTypes} from '@shared/types/form-field';
import {UUID} from 'crypto';
import {combineLatest, lastValueFrom, map, Subscription, tap} from 'rxjs';
import {ResourceDetailsComponent} from './resource-details.component';

@Component({
  standalone: true,
  selector: 'app-resource-access-editor',
  imports: [ReactiveFormsModule, FormlyModule, MatIconModule, MatButtonModule, RouterLink, SubsectionScrollDirective],
  templateUrl: './resource-access.component.html',
  styleUrl: './resource-access.component.scss',
})
export class ResourceAccessComponent implements SectionHeaderActions {
  public objectName: string = 'resource'; //for unsaved data guard

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly resourceService: ResourceService,
    private readonly orgService: OrganizationService,
    private readonly userService: UserService,
    private readonly orgUiService: UiConfigService,
    private readonly snackbarService: MatSnackBar,
    private readonly attestationService: AttestationService,
    private readonly validationErrorHandler: ValidationErrorHandler,
  ) {
    let audiencesLoadedResolver: (value: PromiseLike<undefined>) => void = undefined;
    this._audiencesLoadedPromise = new Promise((resolve, _) => (audiencesLoadedResolver = resolve));
    const audiences = combineLatest([
      this.orgService.getOrganizations(),
      this.orgUiService.getOrganizationUIConfigs(),
      toObservable(this.userService.userSignal),
    ]).pipe(
      map(([orgs, ui, me]) =>
        orgs.organizations
          .sort((a, b) => {
            if (a.full_name == 'Other/External') return 1;
            if (b.full_name == 'Other/External') return -1;
            if (me && a.name == me.organization.name) return -1;
            if (me && b.name == me.organization.name) return 1;
            let sortOrderA: number | undefined =
              (ui.find(uiOrg => uiOrg.organization_id == a.id)?.config_data?.['display_order'] as number) ?? undefined;
            let sortOrderB: number | undefined =
              (ui.find(uiOrg => uiOrg.organization_id == b.id)?.config_data?.['display_order'] as number) ?? undefined;
            if (sortOrderA === undefined || sortOrderB === undefined) return 0;
            return sortOrderA - sortOrderB;
          })
          .map(org => ({
            label: org.full_name == 'Other/External' ? 'Guest Users' : org.full_name,
            value: org.name,
          })),
      ),
      tap(_ => audiencesLoadedResolver(undefined)),
    );
    this._routeChangeSubscription = this.route.data.subscribe(data => {
      const resource = data['resource'] as ResourceResponse & ResourcePermissionsFlags;
      this.id = <UUID>resource.id;
      this.name = <string>resource.name;
      this.objectName = resource.resource_type === 'Generic' ? 'Resource' : resource.resource_type;

      const model = ResourceDetailsComponent.modelMap(resource);
      //HACK Multicheckbox has a different idea of how to model a list of items...
      const audiences = {};
      for (let aud of model.target_audiences) {
        audiences[aud] = true;
      }
      //@ts-expect-error
      this.model = {...model, target_audiences: audiences};
    });

    this.formFields = [
      {
        key: 'owners',
        type: OwnerTableFormFieldComponent,
        props: {
          label: 'Resource Permissions',
        },
      },
      {
        key: 'visible_to_all_users',
        type: FormlyFieldTypes.RADIO,
        className: 'wide-form-field no-outline',
        wrappers: ['group'],
        props: {
          label: 'Visibility of Resource Details',
          required: true,
          options: [
            {label: 'Owners Only', value: false},
            {label: 'All Users', value: true},
          ],
          labelProp: 'label',
          valueProp: 'value',
        },
      },
      {
        key: 'target_audiences',
        type: FormlyFieldTypes.MULTICHECKBOX,
        className: 'wide-form-field no-outline',
        wrappers: ['group'],
        props: {
          label: 'Recommended Audience',
          required: true,
          options: audiences,
          labelProp: 'label',
          valueProp: 'value',
          className: 'auto-width-field',
        },
      },
    ];
  }

  _routeChangeSubscription: Subscription;
  _audiencesLoadedPromise: Promise<undefined>;
  model: ResourceCreate;
  id: UUID;
  name: string;
  modelType: 'Generic' | 'Event';

  formFields: FormlyFieldConfig[];
  form: FormGroup = new UntypedFormGroup({});
  options: FormlyFormOptions = {};

  submitting: boolean = false;

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.status == 'PENDING') {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError(this.formFields);
      return;
    }
    try {
      this.submitting = true;
      await lastValueFrom(
        this.resourceService.updateResource(this.id, {
          ...this.model,
          target_audiences: Object.keys(this.model.target_audiences).filter(key => this.model.target_audiences[key]),
        } as ResourceCreate),
      );
      this.form.markAsPristine();
      await this.router.navigate(['/resource-explorer/view', this.id]);
    } catch (e) {
      this.validationErrorHandler.handleError(this.formFields, <HttpErrorResponse>e);
    } finally {
      this.submitting = false;
    }
  }

  async deleteResource(): Promise<void> {
    if (
      await this.attestationService.confirmWithCancel(
        'Are you sure you want to permanently delete this ' + this.objectName + '?',
        'Delete ' + this.objectName,
      )
    ) {
      try {
        await lastValueFrom(this.resourceService.deleteResource(this.id));
        this.form.markAsPristine();
        this.snackbarService.open(`"${this.model.name}" was deleted.`, undefined, {duration: 5000});
        await this.router.navigate(['/', ROUTE_BASE.RE, 'search']);
      } catch (_) {
        this.snackbarService.open("Couldn't delete.", undefined, {duration: 5000, panelClass: 'snackbar-error'});
      }
    }
  }

  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const actions: SectionHeaderAction[] = [
      {
        target: '/help/resource-explorer/edit-resource-access',
        caption: 'Help',
        color: 'var(--mat-sys-primary)',
        icon: 'help_outline',
      },
    ];

    return actions;
  });
}
